// Single source of truth for "what kind of file is this and how do we preview
// it". Replaces the extension/MIME lists that were copy-pasted across
// FilePreview, DocumentViewerModal, FileTypeIcon and FoldersTable.
//
// `extension` always holds a bare extension ("docx"); the backend column
// was renamed from `content_type`, which used to hold either that or a MIME
// ("image/png", "application/pdf"); the display name lives in `filename`
// (full name with extension). We check both.

export const OFFICE_EXTENSIONS = ['doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'csv', 'ppt', 'pptx', 'odp'];
export const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
// Plain-text-ish formats the browser can render inline. `csv` is intentionally
// excluded here — it's handled by Collabora as an office/spreadsheet type.
export const TEXT_EXTENSIONS = ['txt', 'log', 'md', 'markdown', 'json', 'xml', 'yml', 'yaml', 'html', 'htm', 'ini', 'conf'];

export function fileExtension(file) {
  const name = file?.name || '';
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

// Formats Collabora can actually open for EDITING (office formats + plain
// text). Everything else — yml/json/md/etc — it only views read-only, so we
// refuse to route those to the editor and tell the user instead.
export const COLLABORA_EDITABLE_EXTENSIONS = [...OFFICE_EXTENSIONS, 'txt'];

// Formats that can be enrolled for E-IMZO signing: what Collabora's
// convert-to renders into the signing rendition, plus PDF itself. Mirrors
// `SIGNABLE_EXTENSIONS` in the backend's workflow/services/convert.py.
export const SIGNABLE_EXTENSIONS = ['doc', 'docx', 'odt', 'rtf', 'txt', 'xls', 'xlsx', 'ods', 'ppt', 'pptx', 'odp', 'pdf'];

export function isSignable(file) {
  const ct = (file?.extension || '').toLowerCase();
  const ext = fileExtension(file);
  return SIGNABLE_EXTENSIONS.includes(ext) || SIGNABLE_EXTENSIONS.includes(ct);
}

export function isCollaboraEditable(file) {
  const ct = (file?.extension || '').toLowerCase();
  const ext = fileExtension(file);
  return COLLABORA_EDITABLE_EXTENSIONS.includes(ext) || COLLABORA_EDITABLE_EXTENSIONS.includes(ct);
}

// Returns 'image' | 'pdf' | 'office' | 'text' | 'other'.
export function fileKind(file) {
  if (!file) return 'other';
  const ct = (file.extension || '').toLowerCase();
  const ext = fileExtension(file);
  if (ct.startsWith('image/') || IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (ct.includes('pdf') || ext === 'pdf') return 'pdf';
  if (OFFICE_EXTENSIONS.includes(ext) || OFFICE_EXTENSIONS.includes(ct)) return 'office';
  if (TEXT_EXTENSIONS.includes(ext)) return 'text';
  return 'other';
}
